import { useCallback } from 'react';
import type { useHistory } from 'react-router-dom';
import type { useSnackbar } from '@applied/dragonfruit/src/components/PageSnackbar/snackbar_provider';
import useDebounce from '@applied/utils/hooks/useDebounce';
import type { useQueryClient } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';
import { invalidateQueryKeys, showAndLogError } from 'api/request';

const DEBOUNCE_TIMEOUT_MS = 500;

type SliderMutationVariables = {
  newValue: number;
};

export type UseSliderSubdivisionsParams = {
  fieldName: string;
  updateFunction: (history: ReturnType<typeof useHistory>, value: number) => Promise<unknown>;
  setDisplayValue: (value: number) => void;
  errorMessage: string;
  committedValue: number | undefined;
  setCommittedValue: (value: number) => void;
  history: ReturnType<typeof useHistory>;
  queryClient: ReturnType<typeof useQueryClient>;
  snackbar: ReturnType<typeof useSnackbar>;
};

export type UseSliderSubdivisionsResult = {
  handleChange: (value: number) => void;
};

export function useSliderSubdivisions({
  fieldName,
  updateFunction,
  setDisplayValue,
  errorMessage,
  committedValue,
  setCommittedValue,
  history,
  queryClient,
  snackbar,
}: UseSliderSubdivisionsParams): UseSliderSubdivisionsResult {
  const sliderValueMutation = useMutation({
    mutationFn: async ({ newValue }: SliderMutationVariables) => {
      const response = await updateFunction(history, newValue);
      if (!response) {
        throw new Error(`Failed to update ${fieldName}`);
      }
      return response;
    },
    onSuccess: async (_data, variables) => {
      setCommittedValue(variables.newValue);
      await invalidateQueryKeys(queryClient);
    },
    onError: (error: Error) => {
      if (committedValue !== undefined) {
        setDisplayValue(committedValue);
      }
      showAndLogError(snackbar, errorMessage, error);
    },
  });

  const debouncedSliderMutate = useDebounce(
    (variables: SliderMutationVariables) => sliderValueMutation.mutate(variables),
    DEBOUNCE_TIMEOUT_MS,
  );

  const handleChange = useCallback(
    (value: number) => {
      setDisplayValue(value);
      debouncedSliderMutate({ newValue: value });
    },
    [setDisplayValue, debouncedSliderMutate],
  );

  return {
    handleChange,
  };
}
